import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Download } from "lucide-react";

export default function GenerateFeatureImages() {
  const [loading, setLoading] = useState(false);
  const [smartApplyImage, setSmartApplyImage] = useState("");
  const [atsTrackerImage, setAtsTrackerImage] = useState("");

  const generateSmartApplyImage = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-feature-image", {
        body: {
          prompt: "Create a modern, professional dashboard mockup showing an AI job application automation system. The interface should display: a queue of job applications being processed automatically, multiple job cards with company logos and positions, progress indicators showing applications being sent, a sidebar with daily application limits and settings, status badges (Applied, In Progress, Success), and a clean dark theme UI with teal/cyan accent colors. The design should look like a SaaS platform dashboard with cards, charts, and professional typography. Ultra high resolution, 16:9 aspect ratio."
        }
      });

      if (error) throw error;
      if (!data?.imageUrl) throw new Error("No image generated");

      setSmartApplyImage(data.imageUrl);
      toast.success("Smart Apply image generated!");
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to generate image");
    } finally {
      setLoading(false);
    }
  };

  const generateAtsTrackerImage = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-feature-image", {
        body: {
          prompt: "Create a modern, professional dashboard mockup showing an ATS score checker and application tracking system. The interface should display: a large circular ATS compatibility score showing 91%, detailed breakdown cards for formatting, keywords, and experience scores, an application tracker table with multiple job entries showing company names, positions, status badges (Interview, Offered, Rejected, Applied), dates, and follow-up reminders, and analytics charts showing response rates. The design should use a dark theme with navy/teal gradient colors, professional typography, and clean card-based layouts. Ultra high resolution, 16:9 aspect ratio."
        }
      });

      if (error) throw error;
      if (!data?.imageUrl) throw new Error("No image generated");

      setAtsTrackerImage(data.imageUrl);
      toast.success("ATS tracker image generated!");
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to generate image");
    } finally {
      setLoading(false);
    }
  };

  const downloadImage = (imageData: string, filename: string) => {
    const link = document.createElement("a");
    link.href = imageData;
    link.download = filename;
    link.click();
  };

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Generate Feature Images</h1>
          <p className="text-muted-foreground">Generate custom mockup images for the landing page features</p>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          {/* Smart Apply Image */}
          <Card className="p-6">
            <h2 className="text-2xl font-bold mb-4">AI Smart Apply Dashboard</h2>
            <Button 
              onClick={generateSmartApplyImage}
              disabled={loading}
              className="w-full mb-4"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                "Generate Smart Apply Image"
              )}
            </Button>
            {smartApplyImage && (
              <div className="space-y-4">
                <img 
                  src={smartApplyImage} 
                  alt="Smart Apply Dashboard"
                  className="w-full rounded-lg border"
                />
                <Button
                  variant="outline"
                  onClick={() => downloadImage(smartApplyImage, "smart-apply-mockup.png")}
                  className="w-full"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Image
                </Button>
              </div>
            )}
          </Card>

          {/* ATS Tracker Image */}
          <Card className="p-6">
            <h2 className="text-2xl font-bold mb-4">ATS Score & Tracker</h2>
            <Button 
              onClick={generateAtsTrackerImage} 
              disabled={loading}
              className="w-full mb-4"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                "Generate ATS Tracker Image"
              )}
            </Button>
            {atsTrackerImage && (
              <div className="space-y-4">
                <img 
                  src={atsTrackerImage} 
                  alt="ATS Score & Tracker" 
                  className="w-full rounded-lg border"
                />
                <Button
                  variant="outline"
                  onClick={() => downloadImage(atsTrackerImage, "ats-tracker-mockup.png")}
                  className="w-full"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download Image
                </Button>
              </div>
            )}
          </Card>
        </div>

        <Card className="p-6 bg-muted">
          <h3 className="font-bold mb-2">Instructions:</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
            <li>Click the generate buttons to create new mockup images</li>
            <li>Download the generated images</li>
            <li>Save them to <code className="bg-background px-1 rounded">src/assets/</code> as:
              <ul className="list-disc list-inside ml-6 mt-1">
                <li><code className="bg-background px-1 rounded">smart-apply-mockup.png</code></li>
                <li><code className="bg-background px-1 rounded">ats-tracker-mockup.png</code></li>
              </ul>
            </li>
            <li>Update the imports in Home.tsx to use the new images</li>
          </ol>
        </Card>
      </div>
    </div>
  );
}
